import api from './api';

// ============================================================
// Helper: trigger browser file download with correct filename
// ============================================================
const triggerFileDownload = (blob: Blob, filename: string) => {
  // Convert blob to base64 data URL — more reliable filename in managed Chrome
  const reader = new FileReader();
  reader.onloadend = () => {
    const a = document.createElement('a');
    a.href = reader.result as string;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 200);
  };
  reader.readAsDataURL(blob);
};

// ============================================================
// Organizations API (minimal — department dropdown)
// ============================================================
export const organizationsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get('/organizations/', { params });
    return response.data;
  },
};

// ============================================================
// KPIs API (minimal — used by talent KPI creation)
// ============================================================
export const kpisApi = {
  create: async (data: any) => {
    const response = await api.post('/kpis/', data);
    return response.data;
  },
};

// ============================================================
// Notifications API (used by Navbar)
// ============================================================
export const notificationsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get('/notifications/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get(`/notifications/${id}/`);
    return response.data;
  },

  markRead: async (id: number) => {
    const response = await api.post(`/notifications/${id}/mark_read/`);
    return response.data;
  },

  markAllRead: async () => {
    const response = await api.post<{ marked_read: number }>('/notifications/mark_all_read/');
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get<{ unread_count: number }>('/notifications/unread_count/');
    return response.data;
  },
};

// ============================================================
// Talent Students API
// ============================================================
export const talentStudentsApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-students/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/talent-students/${id}/`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-students/', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.patch(`/talent-students/${id}/`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-students/${id}/`);
    return response;
  },
  importExcel: async (file: File, specializationId?: number, projectId?: number, activityId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (specializationId) formData.append('specialization_fk', String(specializationId));
    if (projectId) formData.append('project_id', String(projectId));
    if (activityId) formData.append('activity_id', String(activityId));
    const response = await api.post('/talent-students/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  resolveConflicts: async (resolutions: Array<{
    student_id: number | null;
    is_new?: boolean;
    update_data: { name?: string; eid?: string; email?: string; phone?: string };
    specialization_fk?: number;
    project_id?: number;
    activity_id?: number;
  }>) => {
    const response = await api.post('/talent-students/resolve-conflicts/', { resolutions });
    return response.data;
  },
  statistics: async () => {
    const response = await api.get('/talent-students/statistics/');
    return response.data;
  },
  profile: async (id: number) => {
    const response = await api.get(`/talent-students/${id}/profile/`);
    return response.data;
  },
  myProfile: async () => {
    const response = await api.get('/talent-students/me/');
    return response.data;
  },
  downloadTemplate: async () => {
    const response = await api.get('/talent-students/download-template/', { responseType: 'blob', params: { _t: Date.now() } });
    triggerFileDownload(response.data, 'student_template.xlsx');
  },
  exportExcel: async (params?: { specialization_fk?: number; project?: number }) => {
    const response = await api.get('/talent-students/export-excel/', { params, responseType: 'blob' });
    triggerFileDownload(response.data, 'students_export.xlsx');
  },
  removeSpecialization: async (id: number) => {
    const response = await api.post(`/talent-students/${id}/remove-specialization/`);
    return response.data;
  },
  removeProject: async (id: number, projectId: number) => {
    const response = await api.post(`/talent-students/${id}/remove-project/`, { project_id: projectId });
    return response.data;
  },
  schools: async () => {
    const response = await api.get('/talent-students/schools/');
    return response.data;
  },
  deactivate: async (id: number) => {
    const response = await api.delete(`/talent-students/${id}/`);
    return response.data;
  },
  activate: async (id: number) => {
    const response = await api.post(`/talent-students/${id}/activate/`);
    return response.data;
  },
  bulkApproveLinks: async (linkIds: number[]) => {
    const response = await api.post('/talent-students/bulk-approve-links/', { link_ids: linkIds });
    return response.data;
  },
  bulkActivateLinks: async (linkIds: number[]) => {
    const response = await api.post('/talent-students/bulk-activate-links/', { link_ids: linkIds });
    return response.data;
  },
  bulkRejectLinks: async (linkIds: number[], reason?: string) => {
    const response = await api.post('/talent-students/bulk-reject-links/', { link_ids: linkIds, reason: reason || '' });
    return response.data;
  },
};

// ============================================================
// Talent Teachers API
// ============================================================
export const talentTeachersApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-teachers/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/talent-teachers/${id}/`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-teachers/', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.patch(`/talent-teachers/${id}/`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-teachers/${id}/`);
    return response;
  },
  importExcel: async (file: File, specializationId?: number, projectId?: number, activityId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (specializationId) formData.append('specialization_id', String(specializationId));
    if (projectId) formData.append('project_id', String(projectId));
    if (activityId) formData.append('activity_id', String(activityId));
    const response = await api.post('/talent-teachers/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  resolveConflicts: async (resolutions: Array<{
    teacher_id: number | null;
    is_new?: boolean;
    update_data: { name?: string; eid?: string; email?: string; phone?: string };
    row_data?: any;
    specialization_id?: number;
    project_id?: number;
    activity_id?: number;
  }>) => {
    const response = await api.post('/talent-teachers/resolve-conflicts/', { resolutions });
    return response.data;
  },
  downloadTemplate: async () => {
    const response = await api.get('/talent-teachers/download-template/', { responseType: 'blob' });
    triggerFileDownload(response.data, 'teacher_template.xlsx');
  },
  exportExcel: async (params?: { specialization?: number; project?: number }) => {
    const response = await api.get('/talent-teachers/export-excel/', { params, responseType: 'blob' });
    triggerFileDownload(response.data, 'teachers_export.xlsx');
  },
  myProfile: async () => {
    const response = await api.get('/talent-teachers/me/');
    return response.data;
  },
  removeSpecialization: async (id: number) => {
    const response = await api.post(`/talent-teachers/${id}/remove-specialization/`);
    return response.data;
  },
  removeProject: async (id: number, projectId: number) => {
    const response = await api.post(`/talent-teachers/${id}/remove-project/`, { project_id: projectId });
    return response.data;
  },
};

// ============================================================
// Talent Activities API
// ============================================================
export const talentActivitiesApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-activities/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/talent-activities/${id}/`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-activities/', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.patch(`/talent-activities/${id}/`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-activities/${id}/`);
    return response;
  },
  calendar: async (params?: any) => {
    const response = await api.get('/talent-activities/calendar/', { params });
    return response.data;
  },
  setSchedules: async (activityId: number, schedules: { date: string; start_time: string; end_time: string }[]) => {
    const response = await api.post(`/talent-activities/${activityId}/schedules/`, { schedules });
    return response.data;
  },
};

// ============================================================
// Talent Specializations API
// ============================================================
export const talentSpecializationsApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-specializations/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/talent-specializations/${id}/`);
    return response.data;
  },
  details: async (id: number) => {
    const response = await api.get(`/talent-specializations/${id}/details/`);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-specializations/${id}/`);
    return response;
  },
  uploadPlan: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/talent-specializations/${id}/upload-plan/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  downloadPlan: async (id: number) => {
    const response = await api.get(`/talent-specializations/${id}/download-plan/`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plan_file';
    a.click();
    window.URL.revokeObjectURL(url);
  },
  removePlan: async (id: number) => {
    const response = await api.post(`/talent-specializations/${id}/remove-plan/`);
    return response.data;
  },
  removeEntity: async (id: number) => {
    const response = await api.post(`/talent-specializations/${id}/remove-entity/`);
    return response.data;
  },
  uploadPlanAttachment: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/talent-specializations/${id}/upload-plan-attachment/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// ============================================================
// Talent Projects API
// ============================================================
export const talentProjectsApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-projects/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/talent-projects/${id}/`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-projects/', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.patch(`/talent-projects/${id}/`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-projects/${id}/`);
    return response;
  },
  uploadPlan: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/talent-projects/${id}/upload-plan/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  downloadPlan: async (id: number) => {
    const response = await api.get(`/talent-projects/${id}/download-plan/`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plan_file';
    a.click();
    window.URL.revokeObjectURL(url);
  },
  removePlan: async (id: number) => {
    const response = await api.post(`/talent-projects/${id}/remove-plan/`);
    return response.data;
  },
  uploadPlanAttachment: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/talent-projects/${id}/upload-plan-attachment/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// ============================================================
// Talent Dashboard API
// ============================================================
export const talentDashboardApi = {
  get: async () => {
    const response = await api.get('/talent-dashboard/');
    return response.data;
  },
  updateSettings: async (data: FormData) => {
    const response = await api.put('/talent-dashboard/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// ============================================================
// Activity Outputs API
// ============================================================
export const activityOutputsApi = {
  list: async (params?: any) => {
    const response = await api.get('/activity-outputs/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/activity-outputs/${id}/`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/activity-outputs/', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.patch(`/activity-outputs/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/activity-outputs/${id}/`);
    return response;
  },
};

// ============================================================
// Talent KPI Links API
// ============================================================
export const talentKPILinksApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-kpi-links/', { params });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-kpi-links/', data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-kpi-links/${id}/`);
    return response;
  },
};

// ============================================================
// Talent Attachments API
// ============================================================
export const talentAttachmentsApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-attachments/', { params });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-attachments/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-attachments/${id}/`);
    return response;
  },
};

// ============================================================
// Talent Teacher-Project Links API
// ============================================================
export const talentTeacherProjectLinksApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-teacher-project-links/', { params });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-teacher-project-links/', data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-teacher-project-links/${id}/`);
    return response;
  },
};

// ============================================================
// Talent Student-Project Links API
// ============================================================
export const talentStudentProjectLinksApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-student-project-links/', { params });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-student-project-links/', data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-student-project-links/${id}/`);
    return response;
  },
};

// ============================================================
// Talent Student-Specialization Links API
// ============================================================
export const talentStudentSpecializationLinksApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-student-specialization-links/', { params });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-student-specialization-links/', data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-student-specialization-links/${id}/`);
    return response;
  },
};

// ============================================================
// Talent Student-Activity Links API
// ============================================================
export const talentStudentActivityLinksApi = {
  list: async (params?: any) => {
    const response = await api.get('/talent-student-activity-links/', { params });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/talent-student-activity-links/', data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/talent-student-activity-links/${id}/`);
    return response;
  },
};
